import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { CONDITION_MAP, COND_TIERS } from './helpers';

// ─── Pricer, shared ───────────────────────────────────────
// The basket and its arithmetic, with no opinion about how any of it looks.
// Both Pricer tabs — the classic page and its Zone twin — render this same
// hook, which is what stops the two drifting the way the hand-copied wishlist
// price lookup once did.

export const STORE_KEY = 'cs2pt_pricer';

export const SOURCES = [
  { id: 'buff',    label: 'Buff163' },
  { id: 'csfloat', label: 'CSFloat' },
];

export const PRESETS = [70, 80, 85, 90, 95, 100];

/**
 * One item's market price from the chosen feed.
 *
 * The two feeds are shaped differently — Buff nests under `starting_at`,
 * CSFloat is flat — and both are keyed by market_hash_name, which spells wear
 * with a hyphen the picker does not use. Items with no wear tier (cases,
 * stickers, capsules) are keyed by the bare name, so a missing condition price
 * falls back to it rather than reporting nothing.
 *
 * `strict` turns that fallback off. It is what asks "is this item really quoted
 * at this wear?", and without it a Fracture Case answers yes five times over —
 * the bare-name price standing in for every tier.
 */
export function readPrice(map, source, name, condition, strict = false) {
  if (!map || !name) return null;
  const read = key => {
    const entry = map[key];
    if (!entry) return null;
    const p = source === 'buff' ? entry?.starting_at?.price : entry?.price;
    return typeof p === 'number' && isFinite(p) ? p : null;
  };
  if (condition) {
    const hit = read(`${name} (${CONDITION_MAP[condition] || condition})`);
    if (hit != null || strict) return hit;
  }
  return strict ? null : read(name);
}

// A percentage field is empty far more often than it is wrong, so "" reads as
// "follow the global bar" rather than as zero.
export function parsePct(text) {
  if (text == null || String(text).trim() === '') return null;
  const n = parseFloat(text);
  return isFinite(n) ? n : null;
}

/**
 * The basket: what is in it, what it costs, and everything that edits it.
 *
 * State lives in localStorage under one key, so the basket survives a reload
 * and follows you across the Classic/Zone switch instead of being rebuilt.
 */
export function usePricerBasket({ livePrices, csfloatPrices, itemsWithConditions }) {
  const saved = useRef(null);
  if (saved.current === null) {
    try { saved.current = JSON.parse(localStorage.getItem(STORE_KEY) || 'null') || {}; }
    catch { saved.current = {}; }
  }

  const [lines, setLines]     = useState(() => (Array.isArray(saved.current.lines) ? saved.current.lines : []));
  const [source, setSource]   = useState(() => saved.current.source || 'buff');
  const [pctText, setPctText] = useState(() => saved.current.pct ?? '100');
  // Non-null only while the target-total box is being typed in; the rest of the
  // time that field shows the computed ask.
  const [targetDraft, setTargetDraft] = useState(null);

  const nextId = useRef(1);

  useEffect(() => {
    try { localStorage.setItem(STORE_KEY, JSON.stringify({ lines, source, pct: pctText })); }
    catch { /* private mode or a full quota — a scratchpad is not worth an error */ }
  }, [lines, source, pctText]);

  const priceMap  = source === 'buff' ? livePrices : csfloatPrices;
  const loaded    = !!priceMap;
  const globalPct = parsePct(pctText) ?? 100;

  // Wear tiers this item is actually quoted at, in the chosen feed. Used both
  // for the per-line dropdown and to pick a sensible default on add.
  const pricedTiers = useCallback(name =>
    COND_TIERS.filter(t => readPrice(priceMap, source, name, t.value, true) != null)
  , [priceMap, source]);

  const addItem = useCallback(name => {
    const tiers = pricedTiers(name);
    const condition = tiers.length
      ? tiers[0].value
      : (itemsWithConditions?.has(name) ? 'Field Tested' : null);
    setLines(prev => {
      // The same item at the same wear is a quantity, not a second row.
      const at = prev.findIndex(l => l.name === name && (l.condition || null) === condition);
      if (at !== -1) {
        const next = [...prev];
        next[at] = { ...next[at], qty: (parseInt(next[at].qty, 10) || 1) + 1 };
        return next;
      }
      return [...prev, { id: `p${Date.now()}_${nextId.current++}`, name, condition, qty: 1, pct: '' }];
    });
  }, [pricedTiers, itemsWithConditions]);

  const patchLine  = useCallback((id, patch) => setLines(prev => prev.map(l => (l.id === id ? { ...l, ...patch } : l))), []);
  const removeLine = useCallback(id => setLines(prev => prev.filter(l => l.id !== id)), []);
  const clear      = useCallback(() => setLines([]), []);

  const { rows, marketTotal, askTotal, fixed, flex, unpriced, units } = useMemo(() => {
    let marketTotal = 0, askTotal = 0, fixed = 0, flex = 0, unpriced = 0, units = 0;
    const rows = lines.map(l => {
      const qty  = Math.max(1, parseInt(l.qty, 10) || 1);
      const unit = readPrice(priceMap, source, l.name, l.condition);
      const own  = parsePct(l.pct);
      const eff  = own ?? globalPct;
      const market = unit == null ? null : unit * qty;
      const ask    = market == null ? null : market * (eff / 100);
      units += qty;
      if (market == null) unpriced += 1;
      else {
        marketTotal += market;
        askTotal    += ask;
        // Lines carrying their own percentage are held still when the footer
        // back-solves a target total; the rest are what the global bar moves.
        if (own == null) flex += market; else fixed += ask;
      }
      return { ...l, qty, unit, market, ask, eff, custom: own != null };
    });
    return { rows, marketTotal, askTotal, fixed, flex, unpriced, units };
  }, [lines, priceMap, source, globalPct]);

  const effectivePct = marketTotal > 0 ? (askTotal / marketTotal) * 100 : null;

  // Typing a total works the percentage backwards. Lines carrying their own
  // percentage stay where they are, so only the flexible part has to make up
  // the difference — which is why this lands exactly on the number typed.
  const applyTarget = useCallback(text => {
    setTargetDraft(text);
    const want = parseFloat(text);
    if (!isFinite(want) || flex <= 0) return;
    setPctText((((want - fixed) / flex) * 100).toFixed(2));
  }, [fixed, flex]);

  return {
    lines, source, setSource, pctText, setPctText, globalPct,
    priceMap, loaded, sourceLabel: SOURCES.find(s => s.id === source)?.label || source,
    pricedTiers, addItem, patchLine, removeLine, clear,
    rows, marketTotal, askTotal, unpriced, units, effectivePct,
    targetDraft, setTargetDraft, applyTarget,
  };
}

/**
 * The add-an-item search box.
 *
 * Same matching the add-item form uses: every typed word has to appear in the
 * name or in one of the option's alias terms, so "ak red" and "deagle blaze"
 * both land. `cls` is the class prefix, which is the only thing the two themes
 * disagree about.
 */
export function ItemPicker({ searchableOptions, onPick, disabled, cls = 'prc' }) {
  const [query, setQuery]     = useState('');
  const [results, setResults] = useState([]);
  const [open, setOpen]       = useState(false);
  const [active, setActive]   = useState(-1);
  const ref = useRef(null);

  useEffect(() => {
    const away = e => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', away);
    return () => document.removeEventListener('mousedown', away);
  }, []);

  const search = q => {
    const words = q.toLowerCase().trim().split(/\s+/).filter(Boolean);
    if (!words.length) { setResults([]); setOpen(false); setActive(-1); return; }
    const out = [];
    for (const opt of (searchableOptions || [])) {
      if (out.length >= 12) break;
      const name = opt.value.toLowerCase();
      const byName  = words.every(w => name.includes(w));
      const byTerms = !byName && (opt.searchableTerms || []).some(t => words.every(w => t.includes(w)));
      if (byName || byTerms) out.push(opt);
    }
    setResults(out); setOpen(out.length > 0); setActive(-1);
  };

  // The basket is usually built in one burst, so picking keeps the caret where
  // it is and empties the box instead of closing anything.
  const pick = opt => {
    if (!opt) return;
    onPick(opt.value);
    setQuery(''); setResults([]); setOpen(false); setActive(-1);
  };

  const onKeyDown = e => {
    if (!open) return;
    if (e.key === 'ArrowDown')    { e.preventDefault(); setActive(i => Math.min(i + 1, results.length - 1)); }
    else if (e.key === 'ArrowUp') { e.preventDefault(); setActive(i => Math.max(i - 1, 0)); }
    else if (e.key === 'Enter')   { e.preventDefault(); pick(results[active >= 0 ? active : 0]); }
    else if (e.key === 'Escape')  { setOpen(false); }
  };

  return (
    <div className={`${cls}-picker`} ref={ref}>
      <svg viewBox="0 0 24 24" width="15" height="15" fill="none" stroke="currentColor" strokeWidth="2.5">
        <circle cx="11" cy="11" r="6.5" /><path d="M16 16l4 4" />
      </svg>
      <input
        value={query}
        disabled={disabled}
        onChange={e => { setQuery(e.target.value); search(e.target.value); }}
        onFocus={() => query && search(query)}
        onKeyDown={onKeyDown}
        placeholder={disabled ? 'Loading skins…' : 'Search an item to add…'}
      />
      {open && (
        <div className={`${cls}-drop`}>
          {results.map((opt, i) => (
            <button key={opt.value} type="button"
              className={`${cls}-drop-item${i === active ? ' on' : ''}`}
              onMouseEnter={() => setActive(i)}
              onMouseDown={e => { e.preventDefault(); pick(opt); }}>
              {opt.value}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
